/**
 * What a screen looks like when there is nothing in it yet.
 *
 * **There are no illustrations here, and that is the design position rather
 * than a gap.** A cartoon on an empty state is a convention borrowed from
 * consumer software, and it lands badly in a tool someone opens beside a
 * trading platform with real money in it — the tonal distance between a
 * friendly character and a prop-firm drawdown gauge two panels over is exactly
 * the distance between "toy" and "instrument". The mark here is a line icon
 * from the shared set at a larger size, in a muted colour, and nothing else.
 *
 * A redesign is free to disagree. If it does, the thing to change is this
 * component and `--empty-icon`, not twelve screens.
 *
 * **The structure is fixed because the failure it prevents is real.** An empty
 * state that says only "No trades recorded yet" is a dead end: the user cannot
 * tell whether they are waiting, whether something is broken, or whether they
 * were meant to do something. So every one of these answers three questions in
 * order — what is missing, why, and what to do about it. `action` is optional
 * only where there genuinely is nothing to do.
 */
import type { ReactNode } from 'react';
import { Icon } from './Icon.js';
import type { IconName } from '@aegis/ui';

export interface EmptyStateProps {
  /** From the shared set — the same mark the section's navigation uses. */
  icon: IconName;
  /** What is missing. A noun phrase, not a sentence: "No trades yet". */
  title: string;
  /** Why it is missing, and what fills it. One or two sentences. */
  children: ReactNode;
  /** The way out. Omit only when there is genuinely nothing the user can do. */
  action?: ReactNode;
  /** Secondary detail — an environment note, a caveat. Rendered dimmer. */
  note?: ReactNode;
}

export function EmptyState({ icon, title, children, action, note }: EmptyStateProps): React.JSX.Element {
  return (
    <div className="empty-state">
      <Icon name={icon} size="lg" className="empty-state-mark" />
      <h3 className="empty-state-title">{title}</h3>
      <div className="empty-state-body">{children}</div>
      {note ? <div className="empty-state-note">{note}</div> : null}
      {action ? <div className="empty-state-action">{action}</div> : null}
    </div>
  );
}

/**
 * A one-line absence inside an otherwise dense panel.
 *
 * The other tier, and deliberately separate. A full empty state inside a
 * 200px-tall analytics card would be absurd — the mark alone would dominate it.
 * This is for absences the user is not blocked by and does not need to act on.
 *
 * If you find yourself wanting to add a button to one of these, it wanted to be
 * an `EmptyState`.
 */
export function InlineEmpty({ children }: { children: ReactNode }): React.JSX.Element {
  return <p className="panel-empty">{children}</p>;
}
